// George Washington University: public college page (#2596), official facts
// only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - GW's Common Data Set 2025-2026 (Office of Institutional Research and
//    Planning PDF): C1 applicants/admits/enrollees for Fall 2025 with the
//    residency table ("in-state" means District of Columbia); C8 test policy
//    for Fall 2027; C21 early decision dates; G1/G5 2026-2027 costs. Its C21
//    count block is still headed "For the Fall 2024 entering class" and is
//    blank; the checkboxes read correctly only in the rendered PDF, not in
//    extracted text.
//  - undergraduate.admissions.gwu.edu: the Apply page (key dates, binding
//    definition), First Year Applicants (dates, portfolio, special programs)
//    and Test Optional.
//  - College Scorecard, school id 131469 (net price by family income), read
//    from the Scorecard site's own API response.
// Nothing here is profile-derived or anyone's odds. Re-check a
// figure against its source before editing it, and move `lastChecked` when
// you do.

export const SOURCES = {
    cds: {
        label: 'GW Common Data Set 2025-2026',
        url: 'https://irp.gwu.edu/sites/g/files/zaxdzs6056/files/2026-04/CDS-PDF-2025-2026.pdf',
        read: '2026-09-30',
    },
    apply: {
        label: 'GW Undergraduate Admissions, Apply',
        url: 'https://undergraduate.admissions.gwu.edu/apply',
        read: '2026-09-30',
    },
    firstYear: {
        label: 'GW Undergraduate Admissions, First Year Applicants',
        url: 'https://undergraduate.admissions.gwu.edu/apply/first-year-applicants',
        read: '2026-09-30',
    },
    tests: {
        label: 'GW Undergraduate Admissions, Test Optional',
        url: 'https://undergraduate.admissions.gwu.edu/apply/test-optional',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?131469-George-Washington-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'george-washington-university',
    kbId: 'george_washington_university',
    name: 'George Washington University',
    shortName: 'GW',
    location: 'Washington, DC',
    control: 'Private',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (GW color palette); decoration only.
    branding: {
        primary: '#033C5A',
        secondary: '#D6BF91',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/george_washington_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 27034,
        admitted: 12741,
        enrolled: 2405,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state (District of Columbia)', applied: 482, admitted: 154 },
            { group: 'Out-of-state', applied: 21934, admitted: 10785 },
            { group: 'International', applied: 3804, admitted: 1380 },
            { group: 'Unknown', applied: 814, admitted: 422 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'GW\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'The Common Data Set lists both early decision rounds but leaves their counts blank, so it gives no early decision acceptance rate.',
        source: 'cds',
        where: 'section C21',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most GW applicants need only the application. Some programs ask for more:',
        rows: [
            { school: 'BFA programs (except Interior Architecture)', requirement: 'A portfolio review: 12 to 20 completed works of art, your best and most recent, sent through SlideRoom under the same major as your application.' },
            { school: 'B.A./B.S.-M.D. program', requirement: 'An additional essay, and SAT or ACT scores (with the ACT Science section, if you send the ACT).' },
            { school: 'University Honors Program', requirement: 'An additional essay.' },
            { school: 'Corcoran Scholars', requirement: 'An additional essay.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Late December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 5', decision: 'Late February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'Late March', binding: false },
        ],
        note: 'The financial aid deadline matches each application deadline. GW has no early action plan.',
        source: 'firstYear',
        binding: 'Both early decision rounds are binding: if you are admitted, you agree to attend GW, pay a non-refundable enrollment fee by your deposit deadline, and withdraw your other applications. Apply under a binding plan to one college only.',
        bindingSource: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'GW is test-optional. You choose on the application whether to send the SAT or ACT; if you do, scores are considered, and if you do not, they are not looked at even when they appear on your transcript. Scores are required for B.A./B.S.-M.D. applicants, homeschooled applicants, and students whose schools give only narrative evaluations. GW superscores both tests.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some applicants for fall 2027, which matches the groups above, with scores due by January 5.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 72000 },
            { item: 'Required fees (first year)', amount: 770 },
            { item: 'Food and housing (on campus, first year)', amount: 18160 },
            { item: 'Books and supplies', amount: 1450 },
            { item: 'Transportation', amount: 1075 },
            { item: 'Other expenses', amount: 1700 },
        ],
        total: 95155,
        note: 'First-year figures. After the first year, required fees are $420 and on-campus food and housing is $21,520.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 35760,
            byIncome: [
                { income: '$0 to $30,000', amount: 19440 },
                { income: '$30,001 to $48,000', amount: 17768 },
                { income: '$48,001 to $75,000', amount: 21338 },
                { income: '$75,001 to $110,000', amount: 28010 },
                { income: 'Over $110,000', amount: 52577 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from GW\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
